import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { API, PAYPAL_CLIENT_ID } from '../constants';
import Confetti from '../components/Confetti';

const DonaPage = () => {
  const [step, setStep] = useState('choose'); // choose | form | success
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', tshirt_name: '', comune: '', message: '', company_name: '', anonymous: false, brand_logo_url: '' });
  const [stats, setStats] = useState({ total_donors: 0, total_amount: 0, goal_donors: 1000, goal_amount: 19000 });
  const [loading, setLoading] = useState(false);
  const [donationResult, setDonationResult] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const tiers = [
    { amount: 1, belt: 'Cintura Bianca', color: 'white', border: 'border-white', reward: 'Badge Digitale per social', img: '/badge_digitale.png', hasTshirt: false },
    { amount: 5, belt: 'Cintura Arancione', color: '#F97316', border: 'border-orange-500', reward: 'Penna Total Black', img: '/penna.png', hasTshirt: false },
    { amount: 10, belt: 'Cintura Verde', color: '#22C55E', border: 'border-green-500', reward: 'Supporto Telefono/Tablet in Bambù', img: '/supporto_bergenes.png', hasTshirt: false },
    { amount: 15, belt: 'Cintura Blu', color: '#3B82F6', border: 'border-blue-500', reward: 'T-shirt personalizzata', img: '/merch_tshirt.png', hasTshirt: true },
    { amount: 19, belt: 'Cintura Marrone', color: '#92400E', border: 'border-amber-700', reward: 'T-shirt + Penna', img: '/merch_tshirt.png', hasTshirt: true },
    { amount: 50, belt: 'Cintura Rossa', color: '#DC2626', border: 'border-red-600', reward: 'Cintura in vera pelle con logo', img: '/cintura_pelle.png', hasTshirt: false, premium: true, paypal: true },
    { amount: 100, belt: 'Gran Maestro', color: '#FFD700', border: 'border-amber-500', reward: 'Cover Tablet + Cover Smartphone + T-shirt + Penna', img: '/cover_bundle.png', hasTshirt: true, premium: true, paypal: true },
    { amount: 999, belt: 'Sponsor Aziendale', color: '#10B981', border: 'border-emerald-500', reward: 'Logo su Sito + Social + Sede Hub · Deducibile 100%', img: '/cover_bundle.png', hasTshirt: true, premium: true, paypal: true, isAzienda: true },
  ];

  useEffect(() => {
    axios.get(`${API}/donations/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setStep('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAmount || !formData.name || !formData.email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/donations`, {
        ...formData,
        amount: selectedAmount,
      });
      setDonationResult(res.data);
      setStep('success');
      setStats(prev => ({ ...prev, total_donors: prev.total_donors + 1, total_amount: prev.total_amount + selectedAmount }));
    } catch (err) {
      alert('Errore durante la donazione. Riprova.');
    }
    setLoading(false);
  };

  const progressPercent = Math.min((stats.total_donors / stats.goal_donors) * 100, 100);
  const currentTier = tiers.find(t => t.amount === selectedAmount);

  if (step === 'success' && donationResult) {
    return (
      <main data-testid="dona-page" className="pt-24 min-h-screen">
        <Confetti active={true} duration={5} />
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-20 h-20 bg-[#00FF41]/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <span className="text-[#00FF41] text-4xl">&#10003;</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Grazie, <span className="text-[#00FF41]">{donationResult.name}</span>
            </h1>
            <p className="text-xl text-zinc-400 mb-8">Sei ufficialmente un membro dell'AI_COVO.</p>
            
            <div className="bg-[#0A0A0A] border-2 p-8 mb-8" style={{ borderColor: currentTier?.color || '#00FF41' }}>
              {currentTier && <img src={currentTier.img} alt={donationResult.reward} className="w-48 h-48 object-contain mx-auto mb-6" />}
              <p className="font-mono text-xs text-[#00FF41] uppercase tracking-wider mb-2">La tua cintura</p>
              <p className="text-3xl font-black mb-2" style={{ color: currentTier?.color || '#00FF41' }}>{donationResult.belt}</p>
              <p className="text-5xl font-black text-[#00FF41] mb-4">{donationResult.amount}€</p>
              <p className="text-zinc-400">{donationResult.reward}</p>
              {donationResult.tshirt_name && (
                <p className="text-white mt-4 font-mono">Nome sulla T-shirt: <strong style={{ color: currentTier?.color }}>{donationResult.tshirt_name}</strong></p>
              )}
            </div>

            <div className="bg-[#00FF41]/10 border border-[#00FF41]/30 p-6 mb-8">
              <p className="text-white font-bold mb-2">Prossimi passi</p>
              <p className="text-zinc-400 text-sm">Ti contatteremo via email per organizzare la consegna del tuo premio. Controlla la casella di posta!</p>
            </div>

            <div className="flex gap-4 justify-center">
              <Link to="/" className="btn-primary bg-[#00FF41] text-black border-[#00FF41] hover:bg-[#33FF66] px-8 py-3" data-testid="dona-back-home">
                Torna alla Home
              </Link>
              <a href={`https://wa.me/393273839782?text=Ciao! Ho appena donato ${donationResult.amount}€ come ${donationResult.belt}!`} target="_blank" rel="noopener noreferrer" className="border border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41]/10 px-8 py-3 transition-colors" data-testid="dona-share-whatsapp">
                Condividi su WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (step === 'form' && selectedAmount) {
    return (
      <main data-testid="dona-page" className="pt-24 min-h-screen">
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-6">
            <button onClick={() => setStep('choose')} className="text-zinc-500 hover:text-white mb-8 font-mono text-sm" data-testid="dona-back-btn">
              &larr; Cambia importo
            </button>

            {/* Selected tier with reward image */}
            <div className="text-center mb-12">
              <p className="font-mono text-xs text-[#00FF41] uppercase tracking-wider mb-4">Hai scelto</p>
              {currentTier && (
                <div className="bg-[#0A0A0A] border-2 p-8 mb-6 inline-block" style={{ borderColor: currentTier.color }}>
                  <img src={currentTier.img} alt={currentTier.reward} className="w-40 h-40 object-contain mx-auto mb-4" data-testid="dona-reward-image" />
                  <p className="text-5xl font-black text-[#00FF41] mb-2">{selectedAmount}€</p>
                  <p className="text-xl font-bold mb-1" style={{ color: currentTier.color }}>{currentTier.belt}</p>
                  <p className="text-zinc-400 text-sm">{currentTier.reward}</p>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="dona-form">
              <div>
                <label className="block text-sm font-mono text-zinc-400 mb-2">Il tuo nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white p-4 focus:border-[#00FF41] focus:outline-none transition-colors"
                  placeholder="Mario Rossi"
                  data-testid="dona-input-name"
                />
              </div>

              <div>
                <label className="block text-sm font-mono text-zinc-400 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white p-4 focus:border-[#00FF41] focus:outline-none transition-colors"
                  placeholder="mario@email.com"
                  data-testid="dona-input-email"
                />
              </div>

              {currentTier?.isAzienda && (
                <div>
                  <label className="block text-sm font-mono text-zinc-400 mb-2">Nome Azienda / Brand *</label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={e => setFormData(p => ({ ...p, company_name: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white p-4 focus:border-[#00FF41] focus:outline-none transition-colors"
                    placeholder="La tua azienda"
                    data-testid="dona-input-company"
                  />
                </div>
              )}

              {currentTier?.hasTshirt && (
                <div>
                  <label className="block text-sm font-mono text-zinc-400 mb-2">Nome sulla T-shirt</label>
                  <input
                    type="text"
                    value={formData.tshirt_name}
                    onChange={e => setFormData(p => ({ ...p, tshirt_name: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white p-4 focus:border-[#00FF41] focus:outline-none transition-colors"
                    placeholder="Il tuo nickname o nome"
                    data-testid="dona-input-tshirt"
                  />
                  <p className="text-zinc-600 text-xs mt-1">Stampato nel colore della tua cintura</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-mono text-zinc-400 mb-2">Comune (opzionale)</label>
                <input
                  type="text"
                  value={formData.comune}
                  onChange={e => setFormData(p => ({ ...p, comune: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white p-4 focus:border-[#00FF41] focus:outline-none transition-colors"
                  placeholder="Colle Val d'Elsa"
                  data-testid="dona-input-comune"
                />
              </div>

              {/* Wall of Fame visibility */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-5 space-y-4">
                <p className="font-mono text-xs text-[#00FF41] uppercase tracking-wider">Wall of Fame</p>
                <p className="text-zinc-400 text-sm">Come vuoi apparire sulla nostra bacheca dei Fondatori?</p>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    data-testid="dona-visibility-public"
                    onClick={() => setFormData(p => ({ ...p, anonymous: false }))}
                    className={`flex-1 py-3 px-4 border-2 font-bold text-sm transition-all ${
                      !formData.anonymous 
                        ? 'border-[#00FF41] bg-[#00FF41]/10 text-[#00FF41]' 
                        : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
                    }`}
                  >
                    Mostra il mio Brand
                  </button>
                  <button
                    type="button"
                    data-testid="dona-visibility-anon"
                    onClick={() => setFormData(p => ({ ...p, anonymous: true, brand_logo_url: '' }))}
                    className={`flex-1 py-3 px-4 border-2 font-bold text-sm transition-all ${
                      formData.anonymous 
                        ? 'border-zinc-400 bg-zinc-800 text-white' 
                        : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
                    }`}
                  >
                    Resta Anonimo
                  </button>
                </div>

                {!formData.anonymous && (
                  <div className="space-y-3 pt-2">
                    <label className="block text-sm font-mono text-zinc-400">
                      Logo del tuo brand (opzionale)
                    </label>
                    <div className="flex items-center gap-4">
                      {formData.brand_logo_url && (
                        <div className="w-14 h-14 border border-[#00FF41]/30 bg-black rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                          <img src={formData.brand_logo_url} alt="Logo preview" className="max-w-full max-h-full object-contain p-1" />
                        </div>
                      )}
                      <label className={`flex-1 border-2 border-dashed py-3 px-4 text-center cursor-pointer transition-all ${
                        uploadingLogo ? 'border-[#00FF41]/50 text-zinc-500' : 'border-zinc-700 hover:border-[#00FF41]/50 text-zinc-400 hover:text-white'
                      }`}>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="hidden"
                          data-testid="dona-logo-upload"
                          disabled={uploadingLogo}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) { alert('Max 2MB'); return; }
                            setUploadingLogo(true);
                            try {
                              const fd = new FormData();
                              fd.append('logo', file);
                              const res = await axios.post(`${API}/donations/upload-logo`, fd);
                              setFormData(p => ({ ...p, brand_logo_url: res.data.url }));
                            } catch { alert('Errore upload. Riprova.'); }
                            setUploadingLogo(false);
                          }}
                        />
                        <span className="font-mono text-xs">
                          {uploadingLogo ? 'Caricamento...' : formData.brand_logo_url ? 'Cambia logo' : 'Carica il tuo logo (PNG, JPG, max 2MB)'}
                        </span>
                      </label>
                    </div>
                    <p className="text-zinc-600 text-xs">Il logo verrà mostrato nella Wall of Fame in homepage</p>
                  </div>
                )}

                {formData.anonymous && (
                  <p className="text-zinc-500 text-xs italic">Il tuo nome non verrà mostrato. Solo la cintura e l'importo saranno visibili.</p>
                )}
              </div>

              {/* Standard submit for non-PayPal tiers */}
              {!currentTier?.paypal && (
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00FF41] text-black font-bold text-xl py-5 hover:bg-[#33FF66] transition-colors disabled:opacity-50"
                  data-testid="dona-submit"
                >
                  {loading ? 'Invio in corso...' : `DONA ${selectedAmount}€`}
                </button>
              )}

              {/* PayPal checkout for premium tiers */}
              {currentTier?.paypal && (
                <div data-testid="paypal-checkout-section">
                  {PAYPAL_CLIENT_ID && PAYPAL_CLIENT_ID !== 'sb' ? (
                    <PayPalScriptProvider options={{
                      clientId: PAYPAL_CLIENT_ID,
                      currency: 'EUR',
                      locale: 'it_IT',
                      enableFunding: 'paylater',
                    }}>
                      <div className="bg-[#0A0A0A] border border-white/10 p-6">
                        <p className="text-white font-bold text-center mb-4">Paga con PayPal</p>
                        <p className="text-zinc-400 text-xs text-center mb-4">Puoi pagare in 3 comode rate senza interessi</p>
                        <PayPalButtons
                          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                          fundingSource={undefined}
                          createOrder={async () => {
                            // First save the donation
                            const donRes = await axios.post(`${API}/donations`, {
                              ...formData,
                              amount: selectedAmount,
                            });
                            setDonationResult(donRes.data);
                            // Then create PayPal order
                            const ppRes = await axios.post(`${API}/paypal/create-order`, {
                              amount: selectedAmount,
                              donation_id: donRes.data.id,
                            });
                            return ppRes.data.id;
                          }}
                          onApprove={async (data) => {
                            await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
                            setStep('success');
                            setStats(prev => ({ ...prev, total_donors: prev.total_donors + 1, total_amount: prev.total_amount + selectedAmount }));
                          }}
                          onError={() => alert('Errore PayPal. Riprova.')}
                        />
                      </div>
                    </PayPalScriptProvider>
                  ) : (
                    <>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#00FF41] text-black font-bold text-xl py-5 hover:bg-[#33FF66] transition-colors disabled:opacity-50"
                        data-testid="dona-submit"
                      >
                        {loading ? 'Invio in corso...' : `REGISTRA DONAZIONE ${selectedAmount}€`}
                      </button>
                      <div className="bg-[#0070BA]/10 border border-[#0070BA]/30 p-4 text-center mt-2">
                        <p className="text-white text-sm font-bold mb-1">PayPal — Paga in 3 rate</p>
                        <p className="text-zinc-400 text-xs">Il pagamento PayPal sarà attivato a breve. Intanto registra la tua donazione e ti contatteremo per il pagamento.</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Payment Gateway Badges */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center">
                <p className="text-zinc-400 text-sm mb-4 font-mono">Dona in tutta sicurezza, scegli come</p>
                <div className="flex flex-wrap justify-center gap-4 items-center">
                  <span className="bg-[#0070BA] text-white text-xs font-bold px-3 py-1.5 rounded" data-testid="gateway-paypal">PayPal <span className="text-[10px] opacity-80">3 rate</span></span>
                  <span className="bg-[#FF9900] text-black text-xs font-bold px-3 py-1.5 rounded" data-testid="gateway-amazon">Amazon Pay</span>
                  <span className="bg-white text-black text-xs font-bold px-3 py-1.5 rounded" data-testid="gateway-apple">Apple Pay</span>
                  <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1.5 rounded" data-testid="gateway-stripe">Stripe</span>
                  <span className="bg-[#EF3E42] text-white text-xs font-bold px-3 py-1.5 rounded" data-testid="gateway-satispay">Satispay</span>
                  <span className="bg-zinc-700 text-white text-xs font-bold px-3 py-1.5 rounded" data-testid="gateway-bonifico">Bonifico</span>
                </div>
              </div>
            </form>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="dona-page" className="pt-24">
      {/* Hero Campaign */}
      <section className="py-24 bg-[#00FF41]/5 border-b border-[#00FF41]/20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FF41] mb-4">La domanda non è SE, ma</p>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-4">
            QUANTO<br />
            <span className="text-[#00FF41]">puoi dare?</span>
          </h1>
          
          <div className="max-w-3xl mx-auto mb-12">
            <p className="text-xl text-zinc-400 leading-relaxed mb-6">
              Non ti chiediamo <span className="line-through text-zinc-600">se</span> vuoi aiutarci.<br />
              Ti chiediamo <strong className="text-white">quanto</strong> puoi contribuire.
            </p>
            <div className="bg-[#00FF41]/10 border border-[#00FF41]/30 p-6 inline-block">
              <p className="text-2xl font-bold text-white">
                1€ × 1000 persone = <span className="text-[#00FF41]">€1.000</span>
              </p>
              <p className="text-zinc-400 mt-2">La tua microdonazione ha un effetto MACRO.</p>
            </div>
          </div>

          {/* Live Progress */}
          <div className="max-w-md mx-auto mb-8">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-500">Fondatori</span>
              <span className="text-[#00FF41] font-mono" data-testid="dona-stats">{stats.total_donors} / {stats.goal_donors.toLocaleString()}</span>
            </div>
            <div className="h-4 bg-zinc-800 overflow-hidden">
              <div className="h-full bg-[#00FF41] transition-all duration-1000" style={{ width: `${progressPercent}%` }}></div>
            </div>
            <p className="text-zinc-500 text-sm mt-2">Raccolti: <span className="text-[#00FF41] font-mono">{stats.total_amount}€</span> / {stats.goal_amount.toLocaleString()}€</p>
          </div>
        </div>
      </section>

      {/* Payment Security Banner */}
      <section className="py-8 border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-zinc-300 font-bold mb-4">Dona in tutta sicurezza, scegli come</p>
          <div className="flex flex-wrap justify-center gap-4 items-center">
            <span className="bg-[#0070BA] text-white text-xs font-bold px-4 py-2 rounded">PayPal <span className="text-[10px] opacity-80 block">paga in 3 rate</span></span>
            <span className="bg-[#FF9900] text-black text-xs font-bold px-4 py-2 rounded">Amazon Pay</span>
            <span className="bg-white text-black text-xs font-bold px-4 py-2 rounded">Apple Pay</span>
            <span className="bg-[#635BFF] text-white text-xs font-bold px-4 py-2 rounded">Stripe</span>
            <span className="bg-[#EF3E42] text-white text-xs font-bold px-4 py-2 rounded">Satispay</span>
            <span className="bg-zinc-700 text-white text-xs font-bold px-4 py-2 rounded">Banco / Bonifico</span>
          </div>
        </div>
      </section>

      {/* Amount Selection */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Scegli la tua cintura</h2>
            <p className="text-zinc-400">Come nel karate, ogni livello ha il suo valore. Clicca per scoprire il tuo premio.</p>
          </div>

          {/* Standard tiers */}
          <p className="font-mono text-xs text-[#00FF41] uppercase tracking-wider mb-4 text-center">Da 1€ a 19€</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            {tiers.filter(t => !t.premium).map(tier => (
              <button
                key={tier.amount}
                onClick={() => handleAmountSelect(tier.amount)}
                data-testid={`dona-${tier.amount}`}
                className={`bg-[#0A0A0A] border-2 ${tier.border} p-5 text-center hover:scale-105 transition-all group`}
              >
                <img src={tier.img} alt={tier.reward} className="w-16 h-16 object-contain mx-auto mb-3 opacity-80 group-hover:opacity-100 transition-opacity" />
                <p className="text-2xl font-black text-[#00FF41] group-hover:scale-110 transition-transform">{tier.amount}€</p>
                <p className="text-zinc-400 text-xs mt-1">{tier.belt}</p>
                <p className="text-zinc-600 text-[10px] mt-1 leading-tight">{tier.reward}</p>
              </button>
            ))}
          </div>

          {/* Premium tiers */}
          <p className="font-mono text-xs text-[#00FF41] uppercase tracking-wider mb-4 text-center">Premi Esclusivi — Pagamento con PayPal in 3 rate</p>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {tiers.filter(t => t.premium && !t.isAzienda).map(tier => (
              <button
                key={tier.amount}
                onClick={() => handleAmountSelect(tier.amount)}
                data-testid={`dona-${tier.amount}`}
                className={`bg-gradient-to-br from-zinc-900 to-zinc-800 border-2 ${tier.border} p-8 text-center hover:scale-105 transition-all group relative`}
              >
                <div className="absolute top-3 right-3 bg-[#0070BA] text-white text-[10px] font-bold px-2 py-1 rounded">PayPal 3 rate</div>
                <img src={tier.img} alt={tier.reward} className="w-32 h-32 object-contain mx-auto mb-4 opacity-80 group-hover:opacity-100 transition-opacity" />
                <p className="text-3xl font-black text-[#00FF41]">{tier.amount}€{tier.amount === 100 ? '+' : ''}</p>
                <p className="text-lg text-white font-bold mt-2">{tier.belt}</p>
                <p className="text-zinc-400 text-sm mt-1">{tier.reward}</p>
              </button>
            ))}
          </div>

          {/* Sponsor Aziendale */}
          <div className="mb-16">
            <button
              onClick={() => handleAmountSelect(999)}
              data-testid="dona-999"
              className="w-full bg-gradient-to-br from-emerald-900/30 to-zinc-900 border-2 border-emerald-500 p-10 text-center hover:scale-[1.02] transition-all group relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-[#00FF41] to-emerald-500"></div>
              <div className="absolute top-4 right-4 bg-[#0070BA] text-white text-xs font-bold px-3 py-1.5 rounded">PayPal 3 rate</div>
              <p className="font-mono text-xs text-emerald-400 uppercase tracking-wider mb-3">Per Aziende e Brand</p>
              <p className="text-5xl font-black text-[#00FF41] mb-2">999€</p>
              <p className="text-2xl text-white font-bold mb-4">Sponsor Aziendale</p>
              <div className="grid md:grid-cols-3 gap-4 text-left max-w-3xl mx-auto mt-6">
                <div className="bg-black/30 p-4 border border-emerald-500/20">
                  <p className="text-emerald-400 font-bold text-sm">Logo su Sito + Social</p>
                  <p className="text-zinc-500 text-xs mt-1">Visibilità sui nostri canali digitali</p>
                </div>
                <div className="bg-black/30 p-4 border border-emerald-500/20">
                  <p className="text-emerald-400 font-bold text-sm">Logo in Sede Hub</p>
                  <p className="text-zinc-500 text-xs mt-1">Targa permanente nello spazio fisico</p>
                </div>
                <div className="bg-black/30 p-4 border border-[#00FF41]/30">
                  <p className="text-[#00FF41] font-bold text-sm">Deducibile 100%</p>
                  <p className="text-zinc-500 text-xs mt-1">Donazione interamente detraibile</p>
                </div>
              </div>
              <p className="text-zinc-500 text-xs mt-6">+ Cover Tablet + Cover Smartphone + T-shirt + Penna</p>
            </button>
          </div>

          {/* Why Give */}
          <div className="bg-[#0A0A0A] border border-white/10 p-8 mb-16">
            <h3 className="text-2xl font-bold text-white mb-6">Perché dare?</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className="text-[#00FF41] text-2xl">→</span>
                <div>
                  <p className="text-white font-bold">Perché TU ne benefici</p>
                  <p className="text-zinc-400 text-sm">Doposcuola per i tuoi figli, formazione per te, spazi per la tua famiglia.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#00FF41] text-2xl">→</span>
                <div>
                  <p className="text-white font-bold">Perché la tua città ne ha bisogno</p>
                  <p className="text-zinc-400 text-sm">L'Italia merita servizi all'altezza del Nord Europa.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#00FF41] text-2xl">→</span>
                <div>
                  <p className="text-white font-bold">Perché nessun altro lo farà</p>
                  <p className="text-zinc-400 text-sm">Se non lo fai tu, chi lo fa? Nessuno lo ha fatto finora.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Social Message */}
          <div className="border border-white/10 p-8 mb-16">
            <h3 className="text-2xl font-bold text-white mb-6">Per chi lo facciamo</h3>
            <div className="space-y-6 text-zinc-400 leading-relaxed">
              <p>
                Per gli <strong className="text-white">immigrati</strong> emarginati. Per le <strong className="text-white">persone che dormono in auto</strong>. Per chi ha perso tutto.
              </p>
              <p>
                Per i <strong className="text-white">giovani connessi ma sconnessi</strong>. Per gli <strong className="text-white">anziani</strong> che non sanno usare lo SPID.
              </p>
            </div>
            <div className="border-l-4 border-[#00FF41] pl-6 mt-8 py-4">
              <p className="text-xl text-white font-bold italic">
                "Non hanno più qualcuno che crede in loro."
              </p>
              <p className="text-2xl text-[#00FF41] font-black mt-2">
                Lo faremo noi. Con il tuo aiuto.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DonaPage;
